// File: src/components/ChatWidget.jsx
// FIXED: Removed invalid top-level useCallback; made closeGlobalSocket a regular function

import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useUser } from '../contexts/UserContext';
import { chatAPI } from '../services/api';
import ChatWindow from './ChatWindow';
import './css/ChatWidget.css';

const SOCKET_SERVER = process.env.REACT_APP_SOCKET_SERVER;

if (!SOCKET_SERVER) {
  console.error('REACT_APP_SOCKET_SERVER is not defined');
}
const GUEST_TOKEN_KEY = 'chat_guest_token';
const CONVO_ID_KEY = 'chat_convo_id';
const MAX_RECONNECT_ATTEMPTS = 5;

// Global singleton refs
let globalSocket = null;
let initPromise = null;
let isInitializing = false;

const getOrCreateSocket = (token) => {
  if (globalSocket && globalSocket.connected) {
    return Promise.resolve(globalSocket);
  }
  
  if (initPromise) {
    return initPromise;
  }

  if (isInitializing) {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!isInitializing && globalSocket) {
          clearInterval(checkInterval);
          resolve(globalSocket);
        }
      }, 50);
    });
  }

  isInitializing = true;
  initPromise = new Promise((resolve, reject) => {
    const newSocket = io(SOCKET_SERVER, {
      auth: { token },
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      globalSocket = newSocket;
      isInitializing = false;
      initPromise = null;
      resolve(globalSocket);
    });

    newSocket.on('disconnect', (reason) => {
      if (reason === 'io server disconnect') {
        // Socket.io handles reconnect
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Global socket connection error:', error);
      isInitializing = false;
      initPromise = null;
      reject(error);
    });

    // Forward events to global namespace for all widgets
    newSocket.on('new-message', (message) => newSocket.emit('global-new-message', message));
    newSocket.on('convo-closed', (data) => newSocket.emit('global-convo-closed', data));
    newSocket.on('convo-reopened', () => newSocket.emit('global-convo-reopened'));
    newSocket.on('admin-joined', (data) => newSocket.emit('global-admin-joined', data));
    newSocket.on('user-typing', (data) => newSocket.emit('global-user-typing', data));
    newSocket.on('message-persisted', (data) => newSocket.emit('global-message-persisted', data));
    newSocket.on('message-error', (error) => newSocket.emit('global-message-error', error));
  });

  return initPromise;
};

// Regular function (no hook)
const closeGlobalSocket = () => {
  if (globalSocket) {
    globalSocket.close();
    globalSocket = null;
    initPromise = null;
    isInitializing = false;
  }
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [convoId, setConvoId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversationStatus, setConversationStatus] = useState('open');
  const [connectionError, setConnectionError] = useState(null);
  const { user } = useUser();
  const previousUserRef = useRef(null);
  const hasInitialized = useRef(false);
  const widgetSocketRef = useRef(null);

  // Init socket once per app lifetime (empty deps)
  useEffect(() => {
    const token = localStorage.getItem('token');
    getOrCreateSocket(token)
      .then((socket) => {
        setIsConnected(socket.connected);
        widgetSocketRef.current = socket;
      })
      .catch((err) => {
        console.error('Failed to init global socket:', err);
        setConnectionError('Echec de connexion. Veuillez actualiser.');
      });

    // Cleanup on unmount (close if no other widgets)
    return closeGlobalSocket;
  }, []); // Empty deps: runs once

  // Load convo once connected
  useEffect(() => {
    if (!hasInitialized.current && isConnected && widgetSocketRef.current) {
      const storedConvoId = localStorage.getItem(CONVO_ID_KEY);
      if (storedConvoId) {
        verifyConversation(storedConvoId);
      }
      hasInitialized.current = true;
    }
  }, [isConnected]);

  // User change for migration
  useEffect(() => {
    handleUserChange();
  }, [user]);

  const handleUserChange = async () => {
    const wasGuest = !previousUserRef.current;
    const isNowLoggedIn = !!user;

    if (wasGuest && isNowLoggedIn) {
      await migrateGuestConversation();
    }

    previousUserRef.current = user;
  };

  const verifyConversation = async (id) => {
    try {
      const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
      const headers = {};
      
      if (guestToken && !user) {
        headers['x-guest-token'] = guestToken;
      }
      
      const { data } = await chatAPI.getConversation(id, headers);
      
      setConvoId(id);
      setConversationStatus(data.conversation.status);
      
      if (!isOpen && data.messages) {
        const unreadAdminMessages = data.messages.filter(
          m => m.senderType === 'admin' && m.is_read === 0
        ).length;
        setUnreadCount(unreadAdminMessages);
      }
      
    } catch (err) {
      console.error('Conversation verification failed:', err);
      
      if (err.response?.status === 404 || err.response?.status === 403) {
        clearConversationData();
      }
    }
  };

  const migrateGuestConversation = async () => {
    const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
    if (!guestToken) return;

    try {
      await chatAPI.migrateGuest({ guestToken });
      
      localStorage.removeItem(GUEST_TOKEN_KEY);
      
      const storedConvoId = localStorage.getItem(CONVO_ID_KEY);
      if (storedConvoId) {
        verifyConversation(storedConvoId);
      }
    } catch (err) {
      console.error('Migration failed:', err);
    }
  };

  const clearConversationData = () => {
    setConvoId(null);
    setConversationStatus('open');
    setUnreadCount(0);
    localStorage.removeItem(CONVO_ID_KEY);
    
    if (user) {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    }
  };

  const showNotification = useCallback((message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Assistance chat', { body: message });
    }
  }, []);

  // Global event listeners (single per widget)
  useEffect(() => {
    if (!widgetSocketRef.current) return;

    const socket = widgetSocketRef.current;

    const handleNewMessage = (message) => {
      if (!isOpen && message.senderType === 'admin') {
        setUnreadCount(prev => prev + 1);
        showNotification("Nouveau message de l'assistance");
      }
    };

    const handleConvoClosed = (data) => {
      setConversationStatus('closed');
      setUnreadCount(0);
      
      if (!isOpen) {
        showNotification("Votre conversation a ete fermee par l'assistance");
      }
    };

    const handleConvoReopened = () => {
      setConversationStatus('open');
    };

    const handleAdminJoined = (data) => {
      if (!isOpen) {
        showNotification(`${data.adminName} a rejoint votre conversation`);
      }
    };

    const handleMessageError = (error) => {
      console.error('Message error:', error);
    };

    socket.on('global-new-message', handleNewMessage);
    socket.on('global-convo-closed', handleConvoClosed);
    socket.on('global-convo-reopened', handleConvoReopened);
    socket.on('global-admin-joined', handleAdminJoined);
    socket.on('global-message-error', handleMessageError);

    return () => {
      socket.off('global-new-message', handleNewMessage);
      socket.off('global-convo-closed', handleConvoClosed);
      socket.off('global-convo-reopened', handleConvoReopened);
      socket.off('global-admin-joined', handleAdminJoined);
      socket.off('global-message-error', handleMessageError);
    };
  }, [isOpen, showNotification]);

  const startChat = useCallback(async (forceNew = false) => {
    if (!isConnected) {
      alert('Connexion perdue. Veuillez patienter et reessayer.');
      return;
    }

    if (conversationStatus === 'closed' || forceNew) {
      clearConversationData();
    }

    let id = convoId;
    
    if (!id) {
      try {
        const guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
        const payload = {};
        
        if (!user && guestToken) {
          payload.guestToken = guestToken;
        }

        const { data } = await chatAPI.startConversation(payload);
        id = data.conversationId;
        
        localStorage.setItem(CONVO_ID_KEY, id);
        
        if (data.guestToken && !user) {
          localStorage.setItem(GUEST_TOKEN_KEY, data.guestToken);
        }
        
        setConvoId(id);
        setConversationStatus('open');
        
        // Join once
        if (widgetSocketRef.current) {
          widgetSocketRef.current.emit('join-convo', id);
        }
        
      } catch (err) {
        console.error('Failed to start chat:', err);
        
        if (err.response?.data?.conversationId) {
          const existingId = err.response.data.conversationId;
          setConvoId(existingId);
          localStorage.setItem(CONVO_ID_KEY, existingId);
          if (widgetSocketRef.current) {
            widgetSocketRef.current.emit('join-convo', existingId);
          }
        } else {
          alert('Impossible de demarrer la conversation. Veuillez reessayer.');
          return;
        }
      }
    }
    
    setIsOpen(true);
    setUnreadCount(0);
  }, [convoId, conversationStatus, user, isConnected]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleNewConvo = useCallback(async (action) => {
    if (action === 'restart') {
      await startChat(true);
    } else if (action === 'closed') {
      clearConversationData();
      setIsOpen(false);
    }
  }, [startChat]);

  const handleConversationClosed = useCallback(() => {
    setConversationStatus('closed');
    setIsOpen(false);
    
    setTimeout(() => {
      clearConversationData();
    }, 1000);
  }, []);

  if (!widgetSocketRef.current) {
    return <div className="chat-widget-container"><div className="spinner">Connexion...</div></div>;
  }

  return (
    <>
      {!isOpen && (
        <div className="chat-widget-container">
          {connectionError && (
            <div className="connection-error-badge">
              ⚠️ {connectionError}
            </div>
          )}
          <button 
            className={`chat-bubble ${!isConnected ? 'disconnected' : ''}`}
            onClick={() => startChat(false)}
            disabled={!isConnected}
            title={
              !isConnected ? 'Connexion...' :
              conversationStatus === 'closed' ? 'Demarrer une nouvelle conversation' : 
              convoId ? 'Continuer la discussion' : 'Besoin d\'aide ?'
            }
          >
            💬 {
              !isConnected ? 'Connexion...' :
              conversationStatus === 'closed' ? 'Nouveau chat' : 
              convoId ? 'Continuer le chat' : 'Besoin d\'aide ?'
            }
            {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
          </button>
        </div>
      )}
      {isOpen && convoId && (
        <ChatWindow
          convoId={convoId}
          socket={widgetSocketRef.current}
          user={user}
          conversationStatus={conversationStatus}
          isConnected={isConnected}
          onClose={closeChat}
          onNewConvo={handleNewConvo}
          onConversationClosed={handleConversationClosed}
        />
      )}
    </>
  );
};

export default ChatWidget;
